import { useCallback, useMemo, type ReactNode } from "react";
import {
  CandlestickSeries,
  HistogramSeries,
  type CandlestickData,
  type ChartOptions,
  type DeepPartial,
  type HistogramData,
  type IChartApi,
  type UTCTimestamp,
} from "lightweight-charts";
import type { Candle, Currency } from "@richrich/contracts";
import { ChartFrame } from "./ChartFrame";
import { normalizeByTime, toUtcTime, useLightweightChart } from "./lw";
import { hexToRgba, paletteFor } from "../lib/palette";
import { useResolvedTheme, type Theme } from "../lib/theme";
import { decimalToNumber, formatCompactMoney } from "../lib/format";

export interface CandleChartProps {
  candles: Candle[];
  /** Overrides the candle currency (defaults to the first candle's). */
  currency?: Currency;
  showVolume?: boolean;
  theme?: Theme;
  height?: number;
  loading?: boolean;
  title?: ReactNode;
  subtitle?: ReactNode;
  className?: string;
}

/**
 * OHLC candlestick chart with an optional volume histogram (ARCHITECTURE §8) —
 * lightweight-charts v5, purpose-built for dense price series. Up/down use the
 * reserved good/critical status hues; volume sits on an overlaid bottom scale.
 */
export function CandleChart({
  candles,
  currency,
  showVolume = true,
  theme,
  height = 320,
  loading = false,
  title = "Price",
  subtitle,
  className,
}: CandleChartProps): ReactNode {
  const resolved = useResolvedTheme(theme);
  const resolvedCurrency: Currency =
    currency ?? (candles[0]?.currency as Currency) ?? ("KRW" as Currency);

  const candleData = useMemo(
    () =>
      normalizeByTime(
        candles.map((c) => ({
          time: toUtcTime(c.timestamp),
          open: decimalToNumber(c.openPrice),
          high: decimalToNumber(c.highPrice),
          low: decimalToNumber(c.lowPrice),
          close: decimalToNumber(c.closePrice),
        })),
      ),
    [candles],
  );

  const volumeData = useMemo(
    () =>
      normalizeByTime(
        candles.map((c) => ({
          time: toUtcTime(c.timestamp),
          value: decimalToNumber(c.volume),
          up: decimalToNumber(c.closePrice) >= decimalToNumber(c.openPrice),
        })),
      ),
    [candles],
  );

  const render = useCallback(
    (chart: IChartApi, palette: ReturnType<typeof paletteFor>) => {
      const candleSeries = chart.addSeries(CandlestickSeries, {
        upColor: palette.good,
        downColor: palette.critical,
        borderUpColor: palette.good,
        borderDownColor: palette.critical,
        wickUpColor: palette.good,
        wickDownColor: palette.critical,
        priceLineVisible: false,
      });
      candleSeries.setData(candleData as CandlestickData<UTCTimestamp>[]);
      candleSeries.priceScale().applyOptions({
        scaleMargins: { top: 0.1, bottom: showVolume ? 0.28 : 0.08 },
      });

      if (showVolume) {
        const volumeSeries = chart.addSeries(HistogramSeries, {
          priceScaleId: "",
          priceFormat: { type: "volume" },
          lastValueVisible: false,
          priceLineVisible: false,
        });
        volumeSeries.setData(
          volumeData.map((v) => ({
            time: v.time,
            value: v.value,
            color: hexToRgba(v.up ? palette.good : palette.critical, 0.45),
          })) as HistogramData<UTCTimestamp>[],
        );
        chart.priceScale("").applyOptions({
          scaleMargins: { top: 0.82, bottom: 0 },
        });
      }
    },
    [candleData, volumeData, showVolume],
  );

  const options = useMemo<DeepPartial<ChartOptions>>(
    () => ({
      localization: { priceFormatter: (v: number) => formatCompactMoney(v, resolvedCurrency) },
    }),
    [resolvedCurrency],
  );

  const { containerRef } = useLightweightChart({ theme: resolved, height, render, options });

  return (
    <ChartFrame
      title={title}
      subtitle={subtitle}
      height={height}
      loading={loading}
      empty={candles.length === 0}
      className={className}
    >
      <div ref={containerRef} style={{ height }} />
    </ChartFrame>
  );
}
